import { useMemo } from 'react';
import { AlertTriangle, KeyRound, MapPin, RefreshCw, ShieldCheck } from 'lucide-react';
import { useI18n, useTranslation } from '@solara/i18n';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  useToast,
} from '@solara/ui-shared';
import { useCertificates, useRotateCertificate, useCompliancePosture } from '@solara/api-client/react';
import { MOCK_HSM } from '../data/mockData';
import { useAuth } from '../auth/AuthContext';
import { LoadingBlock, ErrorBanner } from '../components/QueryState';

export default function CertificatesPage() {
  const { t } = useTranslation('admin');
  const { locale } = useI18n();
  const { can } = useAuth();
  const { toast } = useToast();
  const dateFmt = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' });
  const numberFmt = new Intl.NumberFormat(locale);

  const certsQuery = useCertificates();
  const rotateCert = useRotateCertificate();
  const posture = useCompliancePosture();
  const certs = certsQuery.data ?? [];

  // EmaraTax mTLS expiry is a production-critical watch item: if it lapses,
  // the FTA channel goes dark. Surface it as its own card with a countdown.
  const emaraMtls = useMemo(() => {
    const c = certs.find((x) => x.type === 'mtls-emaratax' || x.type === 'mtls_emaratax');
    if (!c) return null;
    const days = Math.floor((new Date(c.notAfter).getTime() - Date.now()) / 86400_000);
    const tone = days < 0 ? 'danger' : days < 30 ? 'warning' : 'success';
    return { cert: c, days, tone: tone as 'danger' | 'warning' | 'success' };
  }, [certs]);
  const hsmRegion = posture.data?.hsm;

  const stats = useMemo(() => {
    const now = Date.now();
    let active = 0;
    let expiring = 0;
    let expired = 0;
    for (const c of certs) {
      const ms = new Date(c.notAfter).getTime() - now;
      if (ms < 0) expired++;
      else if (ms < 30 * 86400_000) expiring++;
      else active++;
    }
    return { active, expiring, expired };
  }, [certs]);

  async function rotate(id: string, name: string) {
    if (!can('certificates.rotate')) return;
    try {
      await rotateCert.mutateAsync(id);
      toast({
        title: t('certs.rotateToast', { name }),
        description: t('certs.rotateToastBody'),
        tone: 'info',
      });
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Rotate failed', tone: 'danger' });
    }
  }

  if (certsQuery.isLoading) return <LoadingBlock />;
  if (certsQuery.error) return <ErrorBanner error={certsQuery.error} />;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('certs.title')}</h1>
        <p className="text-fg-muted">{t('certs.subtitle')}</p>
      </header>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-success" />
              {t('certs.hsm.title')}
            </CardTitle>
            <Badge tone={MOCK_HSM.status === 'healthy' ? 'success' : MOCK_HSM.status === 'degraded' ? 'warning' : 'danger'}>
              {MOCK_HSM.status.toUpperCase()}
            </Badge>
          </div>
        </CardHeader>
        <CardBody className="grid gap-4 sm:grid-cols-2 text-sm">
          <Row
            label={t('certs.hsm.region')}
            value={
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-success" />
                <span className="font-medium">{hsmRegion ? `${hsmRegion.provider} ${hsmRegion.region}` : MOCK_HSM.region}</span>
                {hsmRegion ? <Badge tone="success">{hsmRegion.regionName}</Badge> : null}
              </span>
            }
          />
          <Row label={t('certs.hsm.primary')} value={<code className="text-xs">{MOCK_HSM.primaryHsm}</code>} />
          <Row label={t('certs.hsm.secondary')} value={<code className="text-xs">{MOCK_HSM.secondaryHsm}</code>} />
          <Row label={t('certs.hsm.signings')} value={numberFmt.format(MOCK_HSM.signingsLast24h)} />
          <Row label={t('certs.hsm.cpu')} value={`${MOCK_HSM.cpuPct}%`} />
          {hsmRegion ? (
            <>
              <Row label="FIPS level" value={hsmRegion.fipsLevel} />
              <Row label="Cluster" value={<code className="text-xs">{hsmRegion.clusterId}</code>} />
            </>
          ) : null}
        </CardBody>
        {hsmRegion ? (
          <CardBody className="pt-0 text-xs text-fg-subtle">{hsmRegion.note}</CardBody>
        ) : null}
      </Card>

      {/* EmaraTax mTLS — dedicated production-critical expiry watch. */}
      {emaraMtls ? (
        <Card className={emaraMtls.tone === 'success' ? '' : emaraMtls.tone === 'warning' ? 'border-warning/40' : 'border-danger/40'}>
          <CardHeader>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <CardTitle className="flex items-center gap-2">
                {emaraMtls.tone === 'success' ? (
                  <ShieldCheck className="h-5 w-5 text-success" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-warning" />
                )}
                EmaraTax mTLS certificate
              </CardTitle>
              <Badge tone={emaraMtls.tone}>
                {emaraMtls.days < 0
                  ? `EXPIRED ${-emaraMtls.days} d ago`
                  : `expires in ${emaraMtls.days} d`}
              </Badge>
            </div>
          </CardHeader>
          <CardBody className="grid gap-4 sm:grid-cols-2 text-sm">
            <Row label="Certificate" value={emaraMtls.cert.name} />
            <Row label="Valid until" value={dateFmt.format(new Date(emaraMtls.cert.notAfter))} />
            <Row label="Issuer" value={<span className="text-xs">{emaraMtls.cert.issuer}</span>} />
            <Row label="Stored in" value={emaraMtls.cert.storedIn} />
            {posture.data ? <Row label="Channel CN" value={<code className="text-xs">{posture.data.emaraTax.mtlsCommonName}</code>} /> : null}
          </CardBody>
          {emaraMtls.days < 30 ? (
            <CardBody className="pt-0">
              <div className="rounded-lg border border-warning/40 bg-warning/5 p-3 text-xs text-warning">
                Production-critical: rotate before expiry or the FTA EmaraTax channel will fail. A lapse blocks all TDD reporting.
              </div>
            </CardBody>
          ) : null}
        </Card>
      ) : null}

      <section className="grid gap-3 sm:grid-cols-3">
        <Stat label={t('certs.kpi.active')} value={numberFmt.format(stats.active)} tone="success" />
        <Stat label={t('certs.kpi.expiring')} value={numberFmt.format(stats.expiring)} tone="warning" />
        <Stat label={t('certs.kpi.expired')} value={numberFmt.format(stats.expired)} tone="danger" />
      </section>

      <Card>
        <CardBody>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-fg-subtle">
                  <th className="text-start font-medium pb-2">{t('certs.table.name')}</th>
                  <th className="text-start font-medium pb-2">{t('certs.table.type')}</th>
                  <th className="text-start font-medium pb-2">{t('certs.table.issuer')}</th>
                  <th className="text-start font-medium pb-2">{t('certs.table.algorithm')}</th>
                  <th className="text-start font-medium pb-2">{t('certs.table.stored')}</th>
                  <th className="text-start font-medium pb-2">Region</th>
                  <th className="text-start font-medium pb-2">{t('certs.table.validUntil')}</th>
                  <th className="text-start font-medium pb-2">{t('certs.table.ocsp')}</th>
                  <th className="text-start font-medium pb-2">{t('certs.table.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {certs.map((c) => {
                  const days = Math.floor((new Date(c.notAfter).getTime() - Date.now()) / 86400_000);
                  const expiringSoon = days < 30 && days >= 0;
                  return (
                    <tr key={c.id}>
                      <td className="py-2.5">
                        <div className="font-medium">{c.name}</div>
                        <div className="text-[10px] text-fg-subtle font-mono break-all">{c.thumbprint.slice(0, 16)}…</div>
                      </td>
                      <td className="py-2.5"><Badge tone="brand">{t(`certs.type.${c.type}`)}</Badge></td>
                      <td className="py-2.5 text-xs">{c.issuer}</td>
                      <td className="py-2.5 text-xs">{c.algorithm}</td>
                      <td className="py-2.5 text-xs">{c.storedIn}</td>
                      <td className="py-2.5 text-xs">
                        {/* All key material is UAE-resident (CloudHSM / KMS / Secrets Manager
                            in me-central-1). CDN edge certs are KMS-managed in-region too. */}
                        <span className="inline-flex items-center gap-1 whitespace-nowrap">
                          <MapPin className="h-3 w-3 text-success" />
                          {hsmRegion?.region ?? 'me-central-1'}
                        </span>
                      </td>
                      <td className="py-2.5 num text-xs">
                        <div>{dateFmt.format(new Date(c.notAfter))}</div>
                        <div className={expiringSoon ? 'text-warning text-[10px]' : 'text-fg-subtle text-[10px]'}>
                          {days < 0 ? `${-days} d ago` : `in ${days} d`}
                        </div>
                      </td>
                      <td className="py-2.5">
                        <Badge tone={c.ocspStatus === 'good' ? 'success' : c.ocspStatus === 'revoked' ? 'danger' : 'warning'}>
                          {t(`certs.ocsp.${c.ocspStatus}`)}
                        </Badge>
                      </td>
                      <td className="py-2.5">
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={!can('certificates.rotate')}
                          onClick={() => rotate(c.id, c.name)}
                          className="gap-1"
                        >
                          <RefreshCw className="h-3.5 w-3.5" />
                          {t('certs.table.rotate')}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone: 'success' | 'warning' | 'danger' }) {
  const accent = {
    success: 'text-success bg-emerald-50',
    warning: 'text-warning bg-amber-50',
    danger: 'text-danger bg-red-50',
  }[tone];
  return (
    <Card>
      <CardBody className="p-4 flex items-center gap-3">
        <div className={`h-10 w-10 grid place-items-center rounded-xl shrink-0 ${accent}`}>
          <KeyRound className="h-4 w-4" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide text-fg-subtle">{label}</div>
          <div className="text-xl font-semibold num">{value}</div>
        </div>
      </CardBody>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-fg-muted">{label}</span>
      <span>{value}</span>
    </div>
  );
}
